import {
  BlinkIdMultiSideRecognizerResult,
  BlinkIdSingleSideRecognizerResult,
  DetectionStatus,
  DisplayableQuad,
  IdBarcodeRecognizerResult,
  RecognizerResultState,
} from '@microblink/blinkid-in-browser-sdk';

import {
  SHORT_BARCODE_VISIBLE_TO_REAL_CAPTURE_WINDOW_HEIGHT_RATIO,
  VISIBLE_TO_REAL_CAPTURE_WINDOW_HEIGHT_RATIO,
  VISIBLE_TO_REAL_CAPTURE_WINDOW_WIDTH_RATIO,
} from 'inquiry/components/ScannerOverlay/ScannerOverlay';
import FrameTrigger, { FrameTriggerConfig } from 'lib/clients/FrameTrigger';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import type { StepGovernmentIdConfig } from 'lib/interfaces/StepsConfig';
import { GovernmentIdSide } from 'lib/interfaces/verificationConfig';
import { isLowLightForDocument } from 'verify/lib/utils/document';
import type { LuminosityData } from 'verify/lib/utils/image';

export enum GovernmentIdMicroblinkTriggerType {
  DetectedGlare,
  DetectedLowLight,
  Capture,
  HasIncompleteBack,
  HoldStill,
  MoveCloser,
  MoveAway,
  NotCentered,
  None,
  Processing,
}

export interface GovernmentIdMicroblinkTriggerParams {
  currentTrigger: GovernmentIdMicroblinkTriggerType;
  microblinkResult:
    | BlinkIdSingleSideRecognizerResult
    | IdBarcodeRecognizerResult
    | BlinkIdMultiSideRecognizerResult
    | null;
  luminosityData: LuminosityData | null;
  /**
   * NOTE: Currently this can be an "empty" quad, e.g. one where detectionStatus is Failed.
   * Consumers can't rely on null checks of this attribute.
   */
  detectedQuad: DisplayableQuad | null;
  videoWidth: number;
  videoHeight: number;
  hasGlare: boolean | null;
  imageDataUri: string | null;
  processingTime?: number | null;
  hasIncompleteBack?: boolean;
  microblinkRecognizerName?: string | null;
  isShortBarcodeOverlay?: boolean;
}

// Was previously 100. Increased to 250 to reduce resource consumption.
export const DETECT_INTERVAL = 250;
const MIN_WIDTHS = {
  [GovernmentIdSide.Front]: 0.6,
  // Microblink only detects the barcode quad when scanning the back of the ID
  [GovernmentIdSide.Back]: 0.3,
  [GovernmentIdSide.FrontOrBack]: 0.6,
  [GovernmentIdSide.BarcodePdf417]: 0.4,
  [GovernmentIdSide.PassportSignature]: 0.4,
};

const MAX_WIDTHS = {
  [GovernmentIdSide.Front]: VISIBLE_TO_REAL_CAPTURE_WINDOW_WIDTH_RATIO + 0.02,
  // Microblink only detects the barcode quad when scanning the back of the ID
  [GovernmentIdSide.Back]: 0.68,
  [GovernmentIdSide.FrontOrBack]: VISIBLE_TO_REAL_CAPTURE_WINDOW_WIDTH_RATIO + 0.02,
  [GovernmentIdSide.BarcodePdf417]: VISIBLE_TO_REAL_CAPTURE_WINDOW_WIDTH_RATIO + 0.02,
  [GovernmentIdSide.PassportSignature]: VISIBLE_TO_REAL_CAPTURE_WINDOW_WIDTH_RATIO + 0.02,
};

type MicroblinkFrameTriggerConfig = Array<
  FrameTriggerConfig<
    GovernmentIdMicroblinkTriggerType,
    GovernmentIdMicroblinkTriggerParams,
    StepGovernmentIdConfig['localizations']['capture-page']
  >
>;

export const GOVERNMENT_ID_FRONT_TRIGGER_CONFIGS: MicroblinkFrameTriggerConfig = [
  {
    type: GovernmentIdMicroblinkTriggerType.DetectedGlare,
    shouldShow: detectsGlare,
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('government-id-scanner.hint.detected-glare'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.MoveCloser,
    shouldShow: (params) => tooFar(GovernmentIdSide.Front, params),
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('government-id-scanner.hint.move-closer'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.MoveAway,
    shouldShow: (params) => tooClose(GovernmentIdSide.Front, params),
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('government-id-scanner.hint.move-away'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.NotCentered,
    shouldShow: (params) => notCentered(params) && !tooFar(GovernmentIdSide.Front, params),
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('government-id-scanner.hint.outside-of-frame'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.DetectedLowLight,
    shouldShow: detectsLowLight,
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('government-id-scanner.hint.detected-low-light'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.None,
    shouldShow: () => true,
    showAfterFrames: 0,
    hideAfterFrames: 0,
    getPrompt: () => '',
  },
];

export const GOVERNMENT_PDF417_BARCODE_TRIGGER_CONFIGS: MicroblinkFrameTriggerConfig = [
  {
    type: GovernmentIdMicroblinkTriggerType.DetectedGlare,
    shouldShow: detectsGlare,
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('government-id-scanner.hint.detected-glare'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.MoveCloser,
    shouldShow: (params) => tooFar(GovernmentIdSide.BarcodePdf417, params),
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('government-id-scanner.hint.move-closer'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.MoveAway,
    shouldShow: (params) => tooClose(GovernmentIdSide.BarcodePdf417, params),
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('government-id-scanner.hint.move-away'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.NotCentered,
    shouldShow: (params) =>
      !!params.isShortBarcodeOverlay &&
      notCentered(params) &&
      !tooFar(GovernmentIdSide.BarcodePdf417, params),
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('government-id-scanner.hint.barcode-outside-of-frame'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.DetectedLowLight,
    shouldShow: detectsLowLight,
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('government-id-scanner.hint.detected-low-light'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.None,
    shouldShow: () => true,
    showAfterFrames: 0,
    hideAfterFrames: 0,
    getPrompt: () => '',
  },
];

export const GOVERNMENT_ID_BACK_TRIGGER_CONFIGS: MicroblinkFrameTriggerConfig = [
  {
    type: GovernmentIdMicroblinkTriggerType.DetectedGlare,
    shouldShow: detectsGlare,
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('government-id-scanner.hint.detected-glare'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.MoveCloser,
    shouldShow: (params) => tooFar(GovernmentIdSide.Back, params),
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('government-id-scanner.hint.move-closer'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.MoveAway,
    shouldShow: (params) => tooClose(GovernmentIdSide.Back, params),
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('government-id-scanner.hint.move-away'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.NotCentered,
    shouldShow: (params) => notCentered(params) && !tooFar(GovernmentIdSide.Front, params),
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('government-id-scanner.hint.outside-of-frame'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.DetectedLowLight,
    shouldShow: detectsLowLight,
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('government-id-scanner.hint.detected-low-light'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.None,
    shouldShow: () => true,
    showAfterFrames: 0,
    hideAfterFrames: 0,
    getPrompt: () => '',
  },
];

export const FULL_BACK_SIDE_TRIGGER_CONFIGS: MicroblinkFrameTriggerConfig = [
  {
    type: GovernmentIdMicroblinkTriggerType.DetectedGlare,
    shouldShow: detectsGlare,
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('government-id-scanner.hint.detected-glare'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.MoveCloser,
    shouldShow: (params) => tooFar(GovernmentIdSide.Back, params, true),
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('government-id-scanner.hint.move-closer'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.MoveAway,
    shouldShow: (params) => tooClose(GovernmentIdSide.Back, params, true),
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('government-id-scanner.hint.move-away'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.NotCentered,
    shouldShow: (params) => notCentered(params) && !tooFar(GovernmentIdSide.Back, params, true),
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('government-id-scanner.hint.outside-of-frame'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.HasIncompleteBack,
    shouldShow: (params) => backIsIncomplete(GovernmentIdSide.Back, params, true),
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('government-id-scanner.hint.incomplete-back'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.DetectedLowLight,
    shouldShow: detectsLowLight,
    showAfterFrames: 2,
    hideAfterFrames: 4,
    getPrompt: ({ t }) => t('government-id-scanner.hint.detected-low-light'),
  },
  {
    type: GovernmentIdMicroblinkTriggerType.None,
    shouldShow: () => true,
    showAfterFrames: 0,
    hideAfterFrames: 0,
    getPrompt: () => '',
  },
];

export const TRIGGER_CONFIGS_BY_SIDE: Record<GovernmentIdSide, MicroblinkFrameTriggerConfig> = {
  [GovernmentIdSide.Front]: GOVERNMENT_ID_FRONT_TRIGGER_CONFIGS,
  [GovernmentIdSide.Back]: GOVERNMENT_ID_BACK_TRIGGER_CONFIGS,
  [GovernmentIdSide.BarcodePdf417]: GOVERNMENT_PDF417_BARCODE_TRIGGER_CONFIGS,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [GovernmentIdSide.FrontOrBack]: undefined,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [GovernmentIdSide.PassportSignature]: undefined,
};

export const PROCESSING_TRIGGER = new FrameTrigger<
  GovernmentIdMicroblinkTriggerType,
  GovernmentIdMicroblinkTriggerParams,
  StepGovernmentIdConfig['localizations']['capture-page']
>({
  // This trigger is just used to trigger the screenshot
  type: GovernmentIdMicroblinkTriggerType.Processing,
  shouldShow: () => true,
  showAfterFrames: 0,
  hideAfterFrames: 0,
  getPrompt: ({ t }) =>
    t('government-id-scanner.hint.processing', {
      defaultValue: t('government-id-scanner.hint.hold-still'),
    }),
});

export function detectsGlare(params: GovernmentIdMicroblinkTriggerParams): boolean {
  // Microblink's hasGlare remains until a non-glare capture is detected, so we also check if a quad is detected.
  return (
    !!params.hasGlare &&
    params.detectedQuad != null &&
    params.detectedQuad.detectionStatus !== DetectionStatus.Failed
  );
}

export function detectsLowLight(params: GovernmentIdMicroblinkTriggerParams): boolean {
  return isLowLightForDocument(params.luminosityData);
}

export function microblinkDetectedId(params: GovernmentIdMicroblinkTriggerParams): boolean {
  return params.microblinkResult?.state === RecognizerResultState.Valid;
}

export function tooFar(
  side: GovernmentIdSide,
  params: GovernmentIdMicroblinkTriggerParams,
  requireFullBack?: boolean,
): boolean {
  const quad = params.detectedQuad;
  if (!quad || quad.detectionStatus === DetectionStatus.Failed) {
    return false;
  }

  const quadWidth = Math.max(
    quad.topRight.x - quad.topLeft.x,
    quad.bottomRight.x - quad.bottomLeft.x,
  );

  // if require full back, use same width requirements as front side
  const minWidth =
    side === GovernmentIdSide.Back && requireFullBack
      ? MIN_WIDTHS[GovernmentIdSide.Front]
      : MIN_WIDTHS[side];

  if (quadWidth > 0 && quadWidth / params.videoWidth < minWidth) {
    return true;
  }

  return params.detectedQuad?.detectionStatus === DetectionStatus.CameraTooFar;
}

export function tooClose(
  side: GovernmentIdSide,
  params: GovernmentIdMicroblinkTriggerParams,
  requireFullBack?: boolean,
): boolean {
  const quad = params.detectedQuad;
  if (!quad || quad.detectionStatus === DetectionStatus.Failed) {
    return false;
  }
  const quadWidth = Math.max(
    quad.topRight.x - quad.topLeft.x,
    quad.bottomRight.x - quad.bottomLeft.x,
  );

  // if require full back, use same width requirements as front side
  const maxWidth =
    side === GovernmentIdSide.Back && requireFullBack
      ? MAX_WIDTHS[GovernmentIdSide.Front]
      : MAX_WIDTHS[side];

  if (quadWidth > 0 && quadWidth / params.videoWidth > maxWidth) {
    return true;
  }
  return params.detectedQuad?.detectionStatus === DetectionStatus.CameraTooClose;
}

// We don't use DetectionStatus.DocumentTooCloseToCameraEdge and
// DetectionStatus.DocumentPartiallyVisible as they are less reliable.
export function notCentered(params: GovernmentIdMicroblinkTriggerParams): boolean {
  const quad = params.detectedQuad;
  if (!quad || quad.detectionStatus === DetectionStatus.Failed) {
    return false;
  }
  const topMost = Math.min(quad.topLeft.y, quad.topRight.y);
  const bottomMost = Math.max(quad.bottomLeft.y, quad.bottomRight.y);
  const leftMost = Math.min(quad.topLeft.x, quad.bottomLeft.x);
  const rightMost = Math.max(quad.topRight.x, quad.bottomRight.x);

  // the visible captured image will be in the middle 80% of the actual captured video,
  // make sure we are properly centered
  // subtract an additional 0.02 to allow slight collision at the edges.
  const paddingRatioHorizontal = (1 - VISIBLE_TO_REAL_CAPTURE_WINDOW_WIDTH_RATIO) / 2 - 0.02;
  const paddingRatioVertical =
    (1 -
      (params.isShortBarcodeOverlay
        ? SHORT_BARCODE_VISIBLE_TO_REAL_CAPTURE_WINDOW_HEIGHT_RATIO
        : VISIBLE_TO_REAL_CAPTURE_WINDOW_HEIGHT_RATIO)) /
      2 -
    0.02;

  if (
    // ignore case where detected quad is all 0's
    bottomMost > 0 &&
    rightMost > 0 &&
    (topMost < params.videoHeight * paddingRatioVertical ||
      bottomMost > params.videoHeight * (1 - paddingRatioVertical) ||
      leftMost < params.videoWidth * paddingRatioHorizontal ||
      rightMost > params.videoWidth * (1 - paddingRatioHorizontal))
  ) {
    return true;
  }

  return false;
}

// this check is only needed for back side because the barcode recognizer can trigger without valid quads being present
// whereas the front-side autocapture cannot do that (can only be triggered when a valid quad is present)
export function backIsIncomplete(
  side: GovernmentIdSide,
  params: GovernmentIdMicroblinkTriggerParams,
  requireFullBack: boolean,
): boolean {
  if (!requireFullBack || side !== GovernmentIdSide.Back) {
    return false;
  }

  return !!params.hasIncompleteBack;
}

export function validId(params: GovernmentIdMicroblinkTriggerParams): boolean {
  return params.detectedQuad?.detectionStatus === DetectionStatus.Success;
}

export function hasValidMrz(
  governmentIdClass: GovernmentIdClass,
  params: GovernmentIdMicroblinkTriggerParams,
): boolean {
  const isNotPassport = governmentIdClass !== 'pp';
  const mrzIsVerified =
    params.microblinkResult && 'mrz' in params.microblinkResult
      ? params.microblinkResult.mrz.verified === true
      : false;
  return isNotPassport || mrzIsVerified;
}

export function hasValidQuad(
  governmentIdSide: GovernmentIdSide,
  params: GovernmentIdMicroblinkTriggerParams,
  enableFullBack: boolean,
): boolean {
  return (
    validId(params) &&
    !tooFar(governmentIdSide, params, enableFullBack) &&
    !tooClose(governmentIdSide, params, enableFullBack) &&
    !notCentered(params)
  );
}
